// Tree.tsx
import { Attachment } from "@mui/icons-material";
import {
  Box,
  Tooltip,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { SimpleTreeView } from "@mui/x-tree-view/SimpleTreeView";
import { TreeItem } from "@mui/x-tree-view/TreeItem";
import { useMemo, useState, useEffect } from "react";

type Requirement = {
  nodeid: string;
  name: string | null;
  description?: string | null;
  procedure?: string[] | null;
};

type Category = {
  nodeid: string;
  name: string;
  requirements: Requirement[];
};

type Props = {
  categories: Category[];
  onSelect: (nodeid: string) => void;
  selectedId?: string | null;
  setSelectedId: (nodeid: string | null) => void;
  onSourceClick?: any;
  flat?: boolean;

  /** NEW: show a search box above the tree (default false) */
  searchable?: boolean;
  /** NEW: placeholder text for the search box */
  searchPlaceholder?: string;

  seachHiglightColor?: string;
  seachHiglightBgColor?: string;
};

function highlight(
  text: string,
  q: string,
  seachHiglightBgColor: string,
  seachHiglightColor: string
) {
  if (!q) return text;
  const idx = text.toLowerCase().indexOf(q.toLowerCase());
  if (idx === -1) return text;
  const before = text.slice(0, idx);
  const match = text.slice(idx, idx + q.length);
  const after = text.slice(idx + q.length);
  return (
    <>
      {before}
      <span
        style={{
          background: seachHiglightBgColor,
          color: seachHiglightColor,
          padding: "2px",
          borderRadius: "4px",
        }}
      >
        {match}
      </span>
      {after}
    </>
  );
}

const CategoryTree = ({
  categories,
  onSelect,
  selectedId,
  setSelectedId,
  onSourceClick,
  flat = false,
  searchable = false,
  searchPlaceholder = "Search category or requirement…",
  seachHiglightColor,
  seachHiglightBgColor,
}: Props) => {
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState<string[]>([]);

  // `flat` mode is used by the Test Requirements viewer when the business
  // wants requirements listed directly instead of grouped under categories.
  const flatRequirements = useMemo(
    () => categories.flatMap((cat) => cat.requirements),
    [categories]
  );

  // Filter categories/requirements by query
  const filtered = useMemo(() => {
    if (flat) return categories;
    if (!query.trim()) return categories;
    const q = query.trim().toLowerCase();

    return categories
      .map((cat) => {
        const catMatch = cat.name.toLowerCase().includes(q);
        const reqs = cat.requirements.filter((r) =>
          (r.name ?? "").toLowerCase().includes(q)
        );
        if (catMatch || reqs.length) {
          return {
            ...cat,
            requirements: reqs.length ? reqs : cat.requirements,
          };
        }
        return null;
      })
      .filter(Boolean) as Category[];
  }, [categories, flat, query]);

  const filteredFlatRequirements = useMemo(() => {
    // Flat lists only search requirement names because there are no visible
    // category rows in this mode.
    if (!query.trim()) return flatRequirements;
    const q = query.trim().toLowerCase();
    return flatRequirements.filter((req) =>
      (req.name ?? "").toLowerCase().includes(q)
    );
  }, [flatRequirements, query]);

  // Auto-expand matching categories when searching; collapse when cleared
  useEffect(() => {
    if (flat) {
      setExpanded([]);
      return;
    }

    if (query.trim()) {
      setExpanded(filtered.map((c) => c.nodeid));
    } else {
      setExpanded([]);
    }
  }, [flat, query, filtered]);

  return (
    <Box sx={{ minHeight: 300 }}>
      {categories.length ? searchable && (
        <TextField
          size="small"
          fullWidth
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={searchPlaceholder}
          sx={{ mb: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: query ? (
              <InputAdornment position="end">
                <IconButton
                  aria-label="clear search"
                  size="small"
                  onClick={() => setQuery("")}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
      ): ""}

      <SimpleTreeView
        expandedItems={expanded}
        onExpandedItemsChange={(_, ids) => setExpanded(ids as string[])}
        selectedItems={selectedId}
        onSelectedItemsChange={(_, id) => {
          // In flat mode every rendered item is selectable. In grouped mode we
          // keep the original behavior of allowing only requirement leaves.
          if (flat && id) {
            setSelectedId(id as string);
            onSelect(id as string);
            return;
          }

          // Only allow selecting leaves (requirements), not category nodes
          if (id && !categories.some((c) => c.nodeid === id)) {
            setSelectedId(id as string);
            onSelect(id as string);
          }
        }}
      >
        {flat
          ? filteredFlatRequirements.map((req) => {
              // Flat rendering keeps the same source-document action used in
              // grouped trees, but removes the category parent level.
              const reqName = req.name || "(Unnamed Requirement)";
              return (
                <TreeItem
                  key={req.nodeid}
                  itemId={req.nodeid}
                  label={
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <Tooltip
                        title={
                          <span style={{ fontWeight: 600 }}>{reqName}</span>
                        }
                      >
                        <span
                          style={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "inline-block",
                            maxWidth: 240,
                          }}
                        >
                          {typeof highlight(
                            reqName,
                            query,
                            seachHiglightBgColor || "#fff59d",
                            seachHiglightColor || "#000000"
                          ) === "string"
                            ? reqName
                            : highlight(
                                reqName,
                                query,
                                seachHiglightBgColor || "#fff59d",
                                seachHiglightColor || "#000000"
                              )}
                        </span>
                      </Tooltip>

                      <Tooltip title="source document">
                        <Attachment
                          sx={{ marginLeft: "auto", cursor: "pointer" }}
                          fontSize="small"
                          color="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            onSourceClick?.(req);
                          }}
                          titleAccess="Open source document"
                        />
                      </Tooltip>
                    </div>
                  }
                />
              );
            })
          : filtered.map((cat) => (
          <TreeItem
            key={cat.nodeid}
            itemId={cat.nodeid}
            label={
              <Tooltip
                title={<span style={{ fontWeight: 600 }}>{cat.name}</span>}
              >
                <span
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "inline-block",
                    maxWidth: 200,
                  }}
                >
                  {typeof highlight(
                    cat.name,
                    query,
                    seachHiglightBgColor || "#fff59d",
                    seachHiglightColor || "#000000"
                  ) === "string"
                    ? cat.name
                    : highlight(
                        cat.name,
                        query,
                        seachHiglightBgColor || "#fff59d",
                        seachHiglightColor || "#000000"
                      )}
                </span>
              </Tooltip>
            }
          >
            {cat.requirements.map((req) => {
              const reqName = req.name || "(Unnamed Requirement)";
              return (
                <TreeItem
                  key={req.nodeid}
                  itemId={req.nodeid}
                  label={
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <Tooltip
                        title={
                          <span style={{ fontWeight: 600 }}>{reqName}</span>
                        }
                      >
                        <span
                          style={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "inline-block",
                            maxWidth: 200,
                          }}
                        >
                          {typeof highlight(
                            reqName,
                            query,
                            seachHiglightBgColor || "#fff59d",
                            seachHiglightColor || "#000000"
                          ) === "string"
                            ? reqName
                            : highlight(
                                reqName,
                                query,
                                seachHiglightBgColor || "#fff59d",
                                seachHiglightColor || "#000000"
                              )}
                        </span>
                      </Tooltip>

                      <Tooltip title="source document">
                        <Attachment
                          sx={{ marginLeft: 8, cursor: "pointer" }}
                          fontSize="small"
                          color="primary"
                          onClick={(e) => {
                            e.stopPropagation(); // don't select the node
                            onSourceClick?.(req);
                          }}
                          titleAccess="Open source document"
                        />
                      </Tooltip>
                    </div>
                  }
                />
              );
            })}
          </TreeItem>
        ))}
      </SimpleTreeView>
    </Box>
  );
};

export default CategoryTree;
